import React from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import {
  Building,
  Activity,
  Clock,
  CheckCircle,
  Users,
  Plus,
  Bell,
  ArrowRight,
  Droplet,
  MapPin,
  MoreVertical,
  AlertCircle
} from 'react-feather';
import { Hospital } from 'lucide-react';

const RequesterOverview = ({
  dashboardStats,
  recentRequests = [],
  bloodTypeRequirements = [],
  statusDistribution = {},
  loading = false,
  error = null
}) => {
  const navigate = useNavigate();
  // Default values for dashboardStats
  const defaultDashboardStats = {
    totalRequests: 0,
    activeRequests: 0,
    completedRequests: 0,
    totalDonors: 0,
  };

  // Merge provided data with defaults
  const stats = { ...defaultDashboardStats, ...dashboardStats };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0
    }
  };

  // Function to get status badge color
  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">Pending</span>;
      case 'in_progress':
        return <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">In Progress</span>;
      case 'fulfilled':
        return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">Fulfilled</span>;
      case 'partially_fulfilled':
        return <span className="px-2 py-1 text-xs rounded-full bg-teal-100 text-teal-800">Partially Fulfilled</span>;
      case 'cancelled':
        return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">Cancelled</span>;
      default:
        return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  // Function to get urgency badge
  const getUrgencyBadge = (urgency) => {
    switch (urgency) {
      case 'high':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
            <AlertCircle className="h-3 w-3 mr-1" />
            Urgent
          </span>
        );
      case 'medium':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
            Medium
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
            Normal
          </span>
        );
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <AlertCircle className="h-5 w-5 text-red-500" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const renderOverview = () => (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-8"
    >
      {/* Stats Overview with improved visuals */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <motion.div
          variants={itemVariants}
          className="bg-gradient-to-br from-red-500 to-red-600 rounded-2xl p-4 text-white shadow-lg hover:shadow-xl transition-shadow duration-300"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-xl">
              <Activity className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-white">{stats.totalRequests}</h3>
              <p className="text-xs text-white/80">Total Requests</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          variants={itemVariants}
          className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-4 text-white shadow-lg hover:shadow-xl transition-shadow duration-300"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-xl">
              <Clock className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-white">{stats.activeRequests}</h3>
              <p className="text-xs text-white/80">Active Requests</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          variants={itemVariants}
          className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-4 text-white shadow-lg hover:shadow-xl transition-shadow duration-300"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-xl">
              <CheckCircle className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-white">{stats.completedRequests}</h3>
              <p className="text-xs text-white/80">Completed Requests</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Additional Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-1 lg:grid-cols-1 gap-4 mb-6">
        <motion.div
          variants={itemVariants}
          className="bg-white rounded-2xl p-5 shadow-lg hover:shadow-xl transition-shadow duration-300"
        >
          <h3 className="text-base font-bold text-gray-900 mb-3">Request Statistics</h3>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <div className="flex justify-between">
                <span className="text-xs font-medium text-gray-700">Total Requests</span>
                <span className="text-xs font-medium text-gray-700">{stats.totalRequests}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="h-2 rounded-full bg-red-500"
                  style={{ width: '100%' }}
                ></div>
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="flex justify-between">
                <span className="text-xs font-medium text-gray-700">Active Requests</span>
                <span className="text-xs font-medium text-gray-700">{stats.activeRequests}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="h-2 rounded-full bg-blue-500"
                  style={{ width: `${stats.totalRequests ? (stats.activeRequests / stats.totalRequests) * 100 : 0}%` }}
                ></div>
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="flex justify-between">
                <span className="text-xs font-medium text-gray-700">Completed Requests</span>
                <span className="text-xs font-medium text-gray-700">{stats.completedRequests}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="h-2 rounded-full bg-green-500"
                  style={{ width: `${stats.totalRequests ? (stats.completedRequests / stats.totalRequests) * 100 : 0}%` }}
                ></div>
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="flex justify-between">
                <span className="text-xs font-medium text-gray-700">Total Donors</span>
                <span className="text-xs font-medium text-gray-700">{stats.totalDonors}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="h-2 rounded-full bg-purple-500"
                  style={{ width: `${stats.totalRequests ? Math.min((stats.totalDonors / stats.totalRequests) * 100, 100) : 0}%` }}
                ></div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Quick Actions with enhanced hover effects */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <motion.div
          variants={itemVariants}
          className="bg-gradient-to-br from-red-500 to-red-600 rounded-2xl p-5 text-white shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer group transform hover:-translate-y-2"
          onClick={() => navigate('/requester-dashboard/create')}
        >
          <div className="flex items-start justify-between">
            <div className="p-2 bg-white/20 rounded-xl">
              <Plus className="w-5 h-5" />
            </div>
            <span className="text-[10px] font-medium bg-white/20 px-2 py-0.5 rounded-full">New Request</span>
          </div>
          <h3 className="text-lg font-bold mt-3">Create Request</h3>
          <p className="text-white/80 text-xs mt-0.5">Create a new blood request</p>
          <div className="flex items-center mt-3 text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">
            Create Now <ArrowRight className="w-3.5 h-3.5 ml-1.5 group-hover:translate-x-1 transition-transform" />
          </div>
        </motion.div>

        <motion.div
          variants={itemVariants}
          className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-5 text-white shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer group transform hover:-translate-y-2"
        >
          <Link to="/requester-dashboard/active" className="block h-full">
            <div className="flex items-start justify-between">
              <div className="p-2 bg-white/20 rounded-xl">
                <Bell className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-medium bg-white/20 px-2 py-0.5 rounded-full">Active</span>
            </div>
            <h3 className="text-lg font-bold mt-3">Track Requests</h3>
            <p className="text-white/80 text-xs mt-0.5">Monitor active requests</p>
            <div className="flex items-center mt-3 text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">
              View Active <ArrowRight className="w-3.5 h-3.5 ml-1.5 group-hover:translate-x-1 transition-transform" />
            </div>
          </Link>
        </motion.div>

        <motion.div
          variants={itemVariants}
          className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-5 text-white shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer group transform hover:-translate-y-2"
        >
          <Link to="/requester-dashboard/history" className="block h-full">
            <div className="flex items-start justify-between">
              <div className="p-2 bg-white/20 rounded-xl">
                <Activity className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-medium bg-white/20 px-2 py-0.5 rounded-full">Reports</span>
            </div>
            <h3 className="text-lg font-bold mt-3">View Reports</h3>
            <p className="text-white/80 text-xs mt-0.5">Track and analyze your requests</p>
            <div className="flex items-center mt-3 text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">
              View History <ArrowRight className="w-3.5 h-3.5 ml-1.5 group-hover:translate-x-1 transition-transform" />
            </div>
          </Link>
        </motion.div>
      </div>

      {/* Recent Requests */}
      <motion.div
        variants={itemVariants}
        className="bg-white rounded-2xl shadow-lg p-5 mb-6"
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-base font-bold text-gray-900">Recent Requests</h3>
          <Link to="/requester-dashboard/active" className="text-xs text-red-600 hover:text-red-800 flex items-center font-medium">
            View All <ArrowRight className="w-3.5 h-3.5 ml-1" />
          </Link>
        </div>

        {recentRequests.length === 0 ? (
          <div className="text-center py-6 text-gray-500 text-sm">
            <p>No requests found. Create your first blood request!</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                    Patient
                  </th>
                  <th scope="col" className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                    Blood Type
                  </th>
                  <th scope="col" className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {recentRequests.map((request) => (
                  <tr key={request._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8 rounded-full bg-red-100 flex items-center justify-center">
                          <span className="text-red-600 font-bold text-xs">{request.patientName?.charAt(0) || 'P'}</span>
                        </div>
                        <div className="ml-3">
                          <div className="text-xs font-bold text-gray-900">{request.patientName}</div>
                          <div className="text-[10px] text-gray-500 mt-0.5">
                            {request.urgency && getUrgencyBadge(request.urgency)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        <Droplet className="h-3.5 w-3.5 mr-1 text-red-500" />
                        <span className="px-1.5 inline-flex text-[10px] leading-4 font-semibold rounded-full bg-red-100 text-red-800">
                          {request.bloodType}
                        </span>
                        <span className="ml-2 text-gray-500 text-xs">{request.unitsNeeded} units</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap transform scale-90 origin-left">
                      {getStatusBadge(request.status)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                      {formatDate(request.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>

      {/* Blood Type Requirements */}
      <motion.div
        variants={itemVariants}
        className="bg-white rounded-2xl shadow-lg p-5 mb-6"
      >
        <h3 className="text-base font-bold text-gray-900 mb-4">Blood Type Requirements</h3>

        {bloodTypeRequirements.length === 0 ? (
          <div className="text-center py-6 text-gray-500 text-sm">
            <p>No blood type data available yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {bloodTypeRequirements.map((item) => (
              <div key={item.type} className="bg-gray-50 rounded-lg p-3 text-center hover:bg-gray-100 transition-colors">
                <div className="text-lg font-bold text-red-600 mb-0.5">{item.type}</div>
                <div className="text-xs text-gray-500 font-medium">{item.units} units</div>
              </div>
            ))}
          </div>
        )}
      </motion.div>

      {/* Request Status Distribution */}
      {Object.keys(statusDistribution).length > 0 && (
        <motion.div
          variants={itemVariants}
          className="bg-white rounded-2xl shadow-lg p-5"
        >
          <h3 className="text-base font-bold text-gray-900 mb-4">Request Status Distribution</h3>

          <div className="space-y-3">
            {Object.entries(statusDistribution).map(([status, percentage]) => (
              <div key={status} className="space-y-1.5">
                <div className="flex justify-between">
                  <span className="text-xs font-medium text-gray-700 capitalize">{status.replace('_', ' ')}</span>
                  <span className="text-xs font-medium text-gray-700">{percentage}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${status === 'fulfilled' ? 'bg-green-500' :
                        status === 'partially_fulfilled' ? 'bg-teal-500' :
                          status === 'in_progress' ? 'bg-blue-500' :
                            status === 'pending' ? 'bg-yellow-500' :
                              'bg-gray-500'
                      }`}
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </motion.div>
  );

  return renderOverview();
};

export default RequesterOverview;